export interface ViewerBootstrap {
  assetId: string;
  title: string;
  pageCount: number;
  documentKey: string;
  pageWordCounts: number[];
}

/**
 * Core API origin. Empty locally — the Vite dev proxy (vite.config.ts) forwards
 * /api to core on localhost, so the path stays relative and same-origin. When
 * the viewer is deployed to Cloudflare Pages, core is a different origin, so CI
 * bakes VITE_API_URL to core's base URL and the call is prefixed with it.
 *
 * Without the prefix a deployed relative /api call hits the Pages static host,
 * which serves index.html for the unknown route — the reader then sees
 * "Unexpected token '<'" as that HTML fails to parse as JSON. Mirrors the
 * console's api/base.ts.
 */
const API_BASE = (import.meta.env.VITE_API_URL ?? '').replace(/\/$/, '');

/**
 * URL of the actual PDF bytes for this link. Same token as the bootstrap — the
 * reader is not logged in. PDF.js fetches this to render the real document.
 * Returns 404 for assets uploaded before storage was configured.
 */
export function documentUrl(token: string): string {
  return `${API_BASE}/api/viewer/document?token=${encodeURIComponent(token)}`;
}

export async function fetchBootstrap(token: string): Promise<ViewerBootstrap> {
  const res = await fetch(`${API_BASE}/api/viewer/bootstrap?token=${encodeURIComponent(token)}`);
  if (!res.ok) {
    // Problem-detail body from core. Surface the real reason: an expired link
    // and a deleted asset need different actions from the reader.
    const problem = await res.json().catch(() => null);
    throw new Error(problem?.detail ?? 'This document could not be opened.');
  }
  return res.json();
}
